import { ColDef, GridOptions } from 'ag-grid-community'
import { DealDetail } from 'api'
import {
  createActionsRenderer,
  createGroupedActionRenderer,
  gridOptionsBase,
  isSelectedForRelatedOpenRepoTrades,
  StandardGridColumnId,
  withAllContext
} from 'view/components/datagrid'
import { dealsGridColumnDefs } from '../../deals/components/DealsGridModel'
import { DealsGridColumnId } from 'view/routes/deals/components/DealsGridColumnNames'
import { Locale, Translation } from 'locale'
import { StructuredActionsButton } from './StructuredActionsButton'

const structuredVisibleColumns = [
  StandardGridColumnId.CHECKBOX,
  StandardGridColumnId.GROUPING,
  StandardGridColumnId.ACTIONS,
  DealsGridColumnId.DEAL_STATUS,
  DealsGridColumnId.COLLATERAL_ISIN_OR_BUI,
  DealsGridColumnId.COLLATERAL_NAME,
  DealsGridColumnId.COLLATERAL_BASKET_NAME,
  DealsGridColumnId.SECONDARY_COLLATERAL_NAME,
  DealsGridColumnId.DEAL_DIRECTION,
  DealsGridColumnId.DEAL_NOTIONAL,
  DealsGridColumnId.COLLATERAL_CURRENCY,
  DealsGridColumnId.DEAL_TERM,
  DealsGridColumnId.EVERGREEN_NOTICE_PERIOD,
  DealsGridColumnId.DEAL_START_DATE,
  DealsGridColumnId.DEAL_END_DATE,
  DealsGridColumnId.MARKET_TRADE_DATE,
  DealsGridColumnId.DEAL_RATE,
  DealsGridColumnId.DEAL_FEE,
  DealsGridColumnId.DEAL_HAIRCUT_TYPE,
  DealsGridColumnId.DEAL_HAIRCUT,
  DealsGridColumnId.DEAL_START_CONSIDERATION,
  DealsGridColumnId.DEAL_START_CONSIDERATION_OVERRIDE,
  DealsGridColumnId.DEAL_FUND_ID,
  DealsGridColumnId.COUNTERPARTY_NAME,
  DealsGridColumnId.DEAL_ID,
  DealsGridColumnId.DEAL_TRADE_DATE,
  DealsGridColumnId.DEAL_REPO_TYPE,
  DealsGridColumnId.DEAL_STOCK_BORROWING_LENDING_TYPE,
  DealsGridColumnId.PRODUCT,
  DealsGridColumnId.PRODUCT_SECTOR
]

const ACTIONS: ColDef = {
  colId: StandardGridColumnId.ACTIONS,
  headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
  equals: () => false,
  width: 180,
  minWidth: 180,
  headerComponent: withAllContext(createGroupedActionRenderer(StructuredActionsButton)),
  cellClass: 'action-cell',
  cellRenderer: withAllContext(createActionsRenderer(StructuredActionsButton)),
  pinned: 'left',
  lockPinned: true,
  enableRowGroup: false,
  lockVisible: true,
  filter: false,
  sortable: false
}

export const structuredGridColumnDefs = (isAdmin: boolean, isBuySideOrganisation: boolean) =>
  dealsGridColumnDefs(isAdmin, isBuySideOrganisation)
    .map((colDef: ColDef) => {
      if (colDef.colId === StandardGridColumnId.ACTIONS) {
        return ACTIONS
      }

      return {
        ...colDef,
        hide: 'colId' in colDef && !structuredVisibleColumns.find(col => col === colDef.colId)
      }
    })
    .sort((a, b) => {
      // Use existing `dealsGridColumnDefs` order for column groups
      if (!('colId' in a) || !('colId' in b)) {
        return 0
      }

      // Use existing `dealsGridColumnDefs` order for hidden columns
      if (a.hide) {
        return 0
      }

      // Place visible columns before hidden
      if (a.hide || b.hide) {
        return a.hide ? 1 : -1
      }

      const aIndexOfVisible = structuredVisibleColumns.indexOf(a.colId as DealsGridColumnId)
      const bIndexOfVisible = structuredVisibleColumns.indexOf(b.colId as DealsGridColumnId)

      // Sort visible columns according to `structuredVisibleColumns` array order
      return Math.sign(aIndexOfVisible - bIndexOfVisible)
    })

export const structuredGridOptions: GridOptions<DealDetail> = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.deal.dealId,
  rowClassRules: {
    'ag-row--selected-for-related': isSelectedForRelatedOpenRepoTrades
  }
}
